import type { LocationQueryRaw, RouteLocationRaw } from "vue-router";

import { useReleaseStore } from "@/stores/release-store";

/**
 * Append the active thread revision (`?thread=<id>`) to an entity-navigation
 * target while a thread branch is being viewed.
 *
 * This makes the rendered href honest — hovering a link shows the real
 * destination, including the branch you'll stay on — and keeps you on the
 * branch when you click. It replaces the old `router.beforeEach` guard that
 * re-stamped the param *after* navigation (invisible to the user).
 *
 * Apply it only to design-mode, same-stek entity links/pushes. When no thread
 * branch is active it returns the target unchanged, so it's a no-op in the
 * working-copy / release views.
 */
export const withThreadRevision = (to: RouteLocationRaw): RouteLocationRaw => {
    // Reactive read: in a template binding the link re-resolves when the user
    // enters/leaves a thread branch.
    const threadId = useReleaseStore().activeThreadId;
    if (!threadId) return to;

    if (typeof to === "string") {
        // Entity link strings don't carry their own query, so attach it here.
        return { path: to, query: { thread: threadId } };
    }

    const existingQuery: LocationQueryRaw =
        (to as { query?: LocationQueryRaw }).query ?? {};
    return { ...to, query: { ...existingQuery, thread: threadId } };
};
